import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Check, Loader2, AlertTriangle, Clock } from "lucide-react";

const STATUS_ICON = {
    pending: Clock,
    running: Loader2,
    done: Check,
    failed: AlertTriangle,
};
const STATUS_TEXT = {
    pending: "Queued",
    running: "Generating…",
    done: "Done",
    failed: "Failed",
};
const STATUS_COLOR = {
    pending: "text-muted-foreground",
    running: "text-primary",
    done: "text-accent",
    failed: "text-destructive",
};

/**
 * Polls /api/generation-jobs/{jobId} every 2s until the job reaches a terminal state.
 * Shows per-chapter live progress. Resilient to transient network errors (retries up
 * to MAX_CONSECUTIVE_FAILS before surfacing as failed).
 */
const POLL_MS = 2000;
const MAX_CONSECUTIVE_FAILS = 8; // ~16s of unreachable backend before giving up

export default function GenerationProgress({ jobId, onComplete, onError }) {
    const [job, setJob] = useState(null);

    useEffect(() => {
        if (!jobId) return undefined;
        let cancelled = false;
        let consecutiveFails = 0;
        const poll = async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/generation-jobs/${jobId}`);
                if (cancelled) return;
                consecutiveFails = 0;
                setJob(data);
                if (data.status === "done") {
                    onComplete?.(data);
                    return;
                }
                if (data.status === "failed") {
                    onError?.(data);
                    return;
                }
                setTimeout(poll, POLL_MS);
            } catch (e) {
                if (cancelled) return;
                consecutiveFails += 1;
                if (consecutiveFails >= MAX_CONSECUTIVE_FAILS) {
                    onError?.({
                        error:
                            e?.response?.data?.detail ||
                            "Lost connection to the server. Generation may still be running — refresh the program editor in a minute to check.",
                    });
                    return;
                }
                // Transient error (e.g. brief server restart) — retry
                setTimeout(poll, POLL_MS);
            }
        };
        poll();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    if (!job) {
        return (
            <div className="flex flex-col items-center gap-3 py-10" data-testid="gen-progress-boot">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
                <div className="text-sm text-muted-foreground">Starting generation…</div>
            </div>
        );
    }

    const done = job.chapters_created || 0;
    const total = job.total || (job.chapters?.length ?? 0);
    const pct = total ? Math.round((done / total) * 100) : 0;

    return (
        <div className="space-y-4" data-testid="gen-progress">
            <div>
                <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-foreground">
                        {job.status === "done" ? "Generation complete" : "Generating curriculum…"}
                    </span>
                    <span className="text-muted-foreground" data-testid="gen-progress-count">
                        {done}/{total} chapters
                    </span>
                </div>
                <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
                    <div
                        className={`h-full transition-all ${
                            job.status === "done" ? "bg-accent" : "bg-primary"
                        }`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
            </div>

            <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="gen-progress-list">
                {(job.chapters || []).map((ch) => {
                    const Icon = STATUS_ICON[ch.status] || Clock;
                    return (
                        <div
                            key={ch.idx}
                            className="flex items-center gap-3 text-sm p-2 rounded-sm bg-secondary/30 border border-border"
                            data-testid={`gen-row-${ch.idx}`}
                        >
                            <Icon
                                className={`h-4 w-4 flex-shrink-0 ${STATUS_COLOR[ch.status]} ${
                                    ch.status === "running" ? "animate-spin" : ""
                                }`}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="truncate text-foreground">
                                    <span className="text-muted-foreground">Ch. {ch.idx + 1}:</span>{" "}
                                    {ch.title}
                                </div>
                                {ch.error && (
                                    <div className="text-xs text-destructive truncate">
                                        {ch.error}
                                    </div>
                                )}
                            </div>
                            <span className={`text-xs ${STATUS_COLOR[ch.status]}`}>
                                {STATUS_TEXT[ch.status]}
                                {ch.status === "done" && ch.lessons_created
                                    ? ` · ${ch.lessons_created} lessons`
                                    : ""}
                            </span>
                        </div>
                    );
                })}
            </div>

            {job.failures > 0 && job.status === "done" && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2">
                    {job.failures} chapter(s) failed. You can retry them from the editor.
                </div>
            )}

            {job.status === "failed" && (
                <div className="text-xs bg-destructive/10 border border-destructive/30 rounded-sm p-3 space-y-1">
                    <div className="font-semibold text-destructive">Generation failed</div>
                    <div className="text-foreground/85">
                        {job.error || "An unexpected error occurred."}
                    </div>
                    {/Budget has been exceeded|Max budget/i.test(job.error || "") && (
                        <div className="mt-2 text-foreground/85">
                            Your Emergent LLM key budget is exhausted. Open your profile →{" "}
                            <span className="font-medium">Universal Key → Add Balance</span>{" "}
                            (or enable auto top-up), then retry generation.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
